import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { IconButton } from '@mui/material';
import { SessionState } from '@heygen/liveavatar-web-sdk';
import { useLiveAvatarContextOptional } from '../LiveAvatar/LiveAvatar.context';
import { Button } from '../presentation/Button/Button';
import { AvatarControlBar } from './AvatarControlBar';
import { DigitalTwinLoader } from './DigitalTwinLoader';

type Props = {
  className?: string;
  /** When true, the panel fills all remaining width and the chat panel is hidden. */
  expanded?: boolean;
  /** Called when the user clicks the expand/minimize toggle button. */
  onToggleExpand?: () => void;
  /**
   * When a HeyGen session is expected (avatarId provided + not in text mode),
   * pass true so the panel shows a loading spinner until the stream is ready.
   */
  sessionActive?: boolean;
  /**
   * Called when the user clicks "Reinitialize Session" after the session
   * has disconnected unexpectedly.
   */
  onReinitialize?: () => void;
  /**
   * When provided, children are rendered in place of the placeholder.
   * Pass <LiveAvatar /> here once a HeyGen session is active.
   * When omitted (or undefined), the placeholder is shown instead.
   */
  children?: ReactNode;
};

/**
 * HeyGenPanel — the center column of the Digital Twin layout.
 *
 * Renders children (i.e. <LiveAvatar />) when provided, otherwise falls back
 * to a placeholder. The placeholder remains visible while the HeyGen session
 * token is loading or when no avatar config has been supplied.
 *
 * Panel sizing (flex: 0 0 60%, min-width: 600px) is intentionally generous to
 * accommodate the avatar's minimum render requirements.
 */
export const HeyGenPanel = ({ className, expanded = false, onToggleExpand, sessionActive = false, onReinitialize, children }: Props) => {
  const avatarCtx = useLiveAvatarContextOptional();

  // Show a spinner while the session token is being fetched (no context yet)
  // or while the video stream hasn't started yet (context exists but not ready).
  // Once isStreamReady flips to true the video renders and we hide the spinner.
  const isSessionLoading = sessionActive && (!avatarCtx || !avatarCtx.isStreamReady);

  // Detect a lapsed/dropped session — DISCONNECTED is the terminal state the
  // HeyGen SDK emits when the session ends without our code requesting it.
  const isDisconnected = avatarCtx?.sessionState === SessionState.DISCONNECTED;

  return (
    <Root className={className} expanded={expanded}>
      {/* ContentArea carries overflow:hidden so the video element is clipped
          correctly, while the absolutely-positioned overlays (ExpandButton and
          AvatarControlBar) live at Root level and are never clipped. */}
      <ContentArea>
        {children ?? (
          <Placeholder>
            {/* HeyGen avatar mounts here once LiveAvatarSessionManager provides a token */}
            <PlaceholderText>HeyGen Avatar</PlaceholderText>
          </Placeholder>
        )}
        {isSessionLoading && (
          <LoadingOverlay>
            <DigitalTwinLoader compact />
          </LoadingOverlay>
        )}
        {isDisconnected && onReinitialize && (
          <DisconnectedOverlay>
            <Button onClick={onReinitialize}>
              Reinitialize Session
            </Button>
          </DisconnectedOverlay>
        )}
      </ContentArea>
      <ExpandButton
        size="small"
        onClick={onToggleExpand}
        aria-label={expanded ? 'Minimize avatar' : 'Expand avatar'}
      >
        {expanded
          ? <CloseFullscreenIcon fontSize="small" />
          : <OpenInFullIcon fontSize="small" />}
      </ExpandButton>
      <AvatarControlBar />
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: prop => prop !== 'expanded',
})<{ expanded: boolean }>`
  position: relative;
  flex-grow: ${({ expanded }) => (expanded ? 1 : 0)};
  flex-shrink: 0;
  flex-basis: 60%;
  min-width: ${({ expanded }) => (expanded ? '0' : '600px')};
  /* Panel always fills the available vertical space; the outer Digital Twin
     layout is responsible for constraining the overall height in normal mode
     and allowing it to stretch when expanded. */
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f4f8;
  border-right: ${({ expanded }) => (expanded ? 'none' : '1px solid #e0e0e0')};
  /* overflow is intentionally NOT set here — it lives on ContentArea instead,
     so the absolutely-positioned AvatarControlBar and ExpandButton overlays
     are never clipped regardless of their vertical position. */
  transition: flex-grow 0.3s ease, min-width 0.3s ease;
`;

/**
 * Wraps the avatar video / placeholder and clips overflow so the
 * video element never bleeds outside the panel, without affecting
 * the overlay controls positioned at Root level.
 */
const ContentArea = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
`;

const ExpandButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.72) !important;
  color: rgba(0, 0, 0, 0.65) !important;
  backdrop-filter: blur(4px);
  border-radius: 50%;

  &:hover {
    background-color: rgba(255, 255, 255, 0.92) !important;
  }
`;

const Placeholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Full-coverage overlay inside ContentArea that shows a spinner while the
 * HeyGen session is initialising or the video stream is not yet ready.
 * Semi-transparent so the placeholder/video beneath remains visible.
 */
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(240, 244, 248, 0.8);
  z-index: 5;
`;

const PlaceholderText = styled.p`
  color: #9e9e9e;
  font-size: 16px;
  margin: 0;
`;

/**
 * Full-coverage overlay shown when the HeyGen session has disconnected
 * unexpectedly (e.g. sandbox timeout, network drop). Gives the user a
 * single action to restart the session without leaving the tab.
 */
const DisconnectedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(240, 244, 248, 0.85);
  z-index: 6;
`;
